import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dropzone } from '@/components/ui/dropzone';
import { useDeleteEquipmentPhoto, useUploadEquipmentPhoto } from '../../hooks/useEquipment';
import type { EquipmentPhoto } from '../../types';
import { PhotoThumb } from '../PhotoThumb';
import { Section, SelectField } from '../fields';

const KIND_OPTIONS = [
  { value: 'product', label: 'Product photo' },
  { value: 'install', label: 'ATEK install' },
] as const;

export function EquipmentPhotosTab({ modelId, photos, canWrite }: { modelId: string; photos: EquipmentPhoto[]; canWrite: boolean }) {
  const upload = useUploadEquipmentPhoto();
  const remove = useDeleteEquipmentPhoto();
  const [kind, setKind] = useState<'product' | 'install'>('install');

  return (
    <div className="space-y-5">
      <Section label={`Photos (${photos.length})`}>
        {photos.length === 0 && <p className="text-sm text-muted-foreground">No photos yet.</p>}
        <div className="grid grid-cols-2 gap-3">
          {photos.map((photo) => (
            <figure key={photo.id} className="space-y-1">
              <PhotoThumb photoId={photo.id} alt={photo.caption ?? 'Equipment photo'} className="h-32 w-full" />
              <figcaption className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="truncate">{photo.kind === 'product' ? 'Product' : 'ATEK install'}{photo.caption ? ` · ${photo.caption}` : ''}</span>
                {canWrite && (
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" aria-label="Remove photo"
                    onClick={() => remove.mutate({ id: modelId, photoId: photo.id })}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      {canWrite && (
        <Section label="Add photos">
          <div className="grid grid-cols-2 gap-3">
            <SelectField<'product' | 'install'> id="eq-photo-kind" label="Kind" value={kind} options={KIND_OPTIONS} onChange={(next) => next && setKind(next)} />
          </div>
          <Dropzone multiple disabled={upload.isPending}
            accept={{ 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'], 'image/webp': ['.webp'] }}
            label="Drop JPEG, PNG or WebP photos (up to 10 MB each)"
            onFiles={(files) => files.forEach((file) => upload.mutate({ id: modelId, file, kind }))} />
          <p className="text-xs text-muted-foreground">Install photos are shown to field technicians: no people, customer names or serial numbers.</p>
        </Section>
      )}
    </div>
  );
}
